import {
  black_overlay4,
  gray20,
  gray30,
  gray100,
  gray80,
  orange20,
  orange50,
  white,
} from './colors';

/**
 * アプリ全体の基本フォント。
 * MUI の createTheme で typography.fontFamily を指定しないと既定の Roboto になるため、
 * 独自テーマを作る箇所では必ずこの定数を渡す（#3130）。
 */
export const baseFontFamily = [
  'Noto Sans JP',
  '-apple-system',
  'BlinkMacSystemFont',
  'Segoe UI',
  'Roboto',
  'Oxygen',
  'Ubuntu',
  'Cantarell',
  'Fira Sans',
  'Droid Sans',
  'Helvetica Neue',
  'sans-serif',
].join(',');

/** createTheme に渡す typography 設定（フォント統一用）（#3130） */
export const baseTypography = { fontFamily: baseFontFamily };

/**
 * createTheme に渡す palette 設定（ブランドカラー統一用）（#3153）。
 * primary を指定しないと MUI 既定の青（#1976d2）が使われ、
 * Switch などの色未指定コンポーネントが青くなるため必ずこれを渡す。
 */
export const basePalette = {
  primary: {
    contrastText: white,
    main: orange50,
  },
};

/**
 * createTheme に渡す Switch のスタイル設定（#3153）。
 *
 * MUI の Switch は disabled かつ checked のとき、つまみの色を
 * `lighten(palette.primary.main, 0.62)` で自動生成する。この既定スタイルは
 * 各コンポーネントの sx で `.Mui-checked` だけを指定した場合より詳細度が高く、
 * オレンジ指定が打ち消されて水色（既定の青の淡色）になってしまう。
 * そのため disabled 状態の色をブランドの淡色オレンジで明示的に上書きする。
 */
export const baseSwitchStyle = {
  MuiSwitch: {
    styleOverrides: {
      switchBase: {
        '&.Mui-checked': {
          '& + .MuiSwitch-track': {
            backgroundColor: orange50,
          },
          '&.Mui-disabled': {
            '& + .MuiSwitch-track': {
              backgroundColor: orange50,
            },
            color: orange20,
          },
          color: orange50,
        },
      },
    },
  },
};

/**
 * createTheme に渡す Radio のスタイル設定（#3358）。
 *
 * MUI の Radio は disabled のとき checked でも灰色にする。計算フィールドの
 * 選択肢は人が選び替えられないよう disabled にしているが（#3358）、
 * 灰色になると「何が選ばれているか」が読み取りにくくなる。
 *
 * disabled は「操作できない」ことを示すもので、選択内容を分かりにくくする
 * 必要はないため、選択中の丸はブランドのオレンジのまま残す。
 * 未選択側は灰色のままにして、操作できないことは引き続き伝える。
 * Switch（baseSwitchStyle）と同じ考え方。
 */
export const baseRadioStyle = {
  MuiRadio: {
    styleOverrides: {
      root: {
        '&.Mui-checked.Mui-disabled': {
          color: orange50,
        },
      },
    },
  },
};

/**
 * createTheme に渡す MenuItem のスタイル設定（#3075）。
 *
 * MUI の MenuItem は選択中の項目の背景を `alpha(palette.primary.main, 0.08)`、
 * 選択中かつホバー時を `alpha(palette.primary.main, 0.12)` で自動生成する。
 * ブランドカラーをオレンジにしている（basePalette）ため、そのままだと
 * プルダウンを開いたときに選択中の項目が薄いオレンジで塗られてしまう。
 *
 * 選択状態の強調はチェックや文字色で足りており、背景まで着色すると
 * 「押せる場所」の意味が薄れて見づらいため、グレー系に戻す。
 * トグル（Switch）のオレンジはブランド表現として残す（baseSwitchStyle）。
 */
export const menuItemBackgroundStyle = {
  '&:hover': {
    backgroundColor: gray20,
  },
  // MUI 内部も `&.Mui-selected` で指定しており詳細度が同じだと後勝ちに負ける。
  // クラスを重ねて詳細度を上げ、確実にこちらを適用させる
  '&.Mui-selected.Mui-selected': {
    backgroundColor: gray20,
    '&:hover': {
      backgroundColor: gray30,
    },
    // キーボード操作でフォーカスしたときも primary 由来の色が使われるため揃える
    '&.Mui-focusVisible': {
      backgroundColor: gray30,
    },
  },
};

export const baseMenuItemStyle = {
  MuiMenuItem: {
    styleOverrides: {
      root: menuItemBackgroundStyle,
    },
  },
};

export const spHeaderHeight = '48px';

// 代理ログイン中に画面上部へ固定表示されるバナーの高さ（PC / SP）。
export const proxyBannerHeight = '60px';
export const spProxyBannerHeight = '48px';

// 下部固定ボタンを持たない画面で使う下余白（#3224）。
// 共通レイアウトの paddingBottom(180px) は一覧画面の固定ボタン向けの値のため、
// これらの画面ではこの値に上書きする。spFullContentMinHeight と対で使う。
export const spFullContentPaddingBottom = '16px';

// SP で画面の残り領域いっぱいに広げるための最小高さ（#3224）。
// 共通レイアウト（usePageLayout）の pageContent が持つ marginTop（SPヘッダー × 2、
// 代理モード中はさらに SPバナー分）と、spFullContentPaddingBottom を差し引いた値。
// ぴったり収めることで、縦中央寄せを保ったまま不要な縦スクロールが出ないようにする。
export const spFullContentMinHeight = (isProxyMode = false): string =>
  `calc(100vh - ${spHeaderHeight} - ${spHeaderHeight}${
    isProxyMode ? ` - ${spProxyBannerHeight}` : ''
  } - ${spFullContentPaddingBottom})`;

export const headerHeight = '60px';

export const globalHeaderHeight = '60px';

export const sideBarWidth = '240px';

export const titleMb = {
  '@media screen and (max-width:480px)': {
    marginLeft: 0,
  },
  fontSize: '22px',
  fontWeight: '400',
  marginBottom: '24px',
};

export const textMb = {
  marginBottom: '24px',
};

export const sizeBelowTenMb = (size: number): boolean => size / 1024 / 1024 > 10;

export const w100 = {
  width: '100%',
};

export const formLabelWrapper = {
  fontSize: '12px',
  marginBottom: '8px',
};

export const regularXl = {
  color: gray100,
  fontSize: '22px',
  fontStyle: 'normal',
  fontWeight: '400',
  lineHeight: 'normal',
};

export const regularLg = {
  color: gray100,
  fontSize: '20px',
  fontStyle: 'normal',
  fontWeight: '400',
  lineHeight: 'normal',
};

export const regularLgWhite = {
  color: white,
  fontSize: '20px',
  fontStyle: 'normal',
  fontWeight: '400',
  lineHeight: 'normal',
};

export const regularMd = {
  color: gray100,
  fontSize: '16px',
  fontStyle: 'normal',
  fontWeight: '400',
  lineHeight: 'normal',
};

export const regularMdWhite = {
  color: white,
  fontSize: '16px',
  fontStyle: 'normal',
  fontWeight: '400',
  lineHeight: 'normal',
};

export const regularSm = {
  color: gray100,
  fontSize: '14px',
  fontStyle: 'normal',
  fontWeight: '400',
  lineHeight: 'normal',
};

export const regularXs = {
  color: gray80,
  fontSize: '12px',
  fontStyle: 'normal',
  fontWeight: '400',
  lineHeight: 'normal',
};

export const boldXs = {
  color: orange50,
  fontSize: '12px',
  fontStyle: 'normal',
  fontWeight: '700',
  lineHeight: 'normal',
};

export const boldMd = {
  color: orange50,
  fontSize: '14px',
  fontStyle: 'normal',
  fontWeight: '700',
  lineHeight: 'normal',
};

export const boldSm = {
  color: orange50,
  fontSize: '16px',
  fontStyle: 'normal',
  fontWeight: '700',
  lineHeight: 'normal',
};

export const boldMdWhite = {
  color: white,
  fontSize: '16px',
  fontWeight: '700',
};

export const boldXxl = {
  color: gray100,
  fontSize: '24px',
  fontStyle: 'normal',
  fontWeight: '700',
  lineHeight: 'normal',
};

export const spHeaderTitle = {
  color: gray100,
  fontSize: '20px',
  fontStyle: 'normal',
  fontWeight: '700',
  lineHeight: 'normal',
};

export const mediumLg = {
  fontSize: '18px',
  fontStyle: 'normal',
  fontWeight: '500',
  lineHeight: 'normal',
};

export const boldMdGray = {
  color: gray80,
  fontSize: '16px',
  fontStyle: 'normal',
  fontWeight: '700',
  lineHeight: 'normal',
};

export const formWideMb = {
  marginBottom: '24px',
};

export const formNarrowMb = {
  marginBottom: '8px',
};

export const formExtraNarrowMb = {
  marginBottom: '0px',
};

export const sectionTitle = {
  color: gray100,
  fontFamily: 'Hiragino Sans',
  fontSize: '20px',
  fontStyle: 'normal',
  fontWeight: '500',
  lineHeight: 'normal',
  letterSpacing: '0.188px',
};

export const infoLabel = {
  color: '#6B7280',
  fontFamily: 'Noto Sans JP',
  fontSize: '16px',
  fontStyle: 'normal',
  fontWeight: '400',
  lineHeight: '20px',
};

export const infoValue = {
  color: gray100,
  fontFamily: 'Noto Sans JP',
  fontSize: '16px',
  fontStyle: 'normal',
  fontWeight: '400',
  lineHeight: '24px',
};

export const dialogTitle = {
  color: gray100,
  fontFamily: 'Noto Sans JP',
  fontSize: '22px',
  fontStyle: 'normal',
  fontWeight: 500,
  lineHeight: 'normal',
  letterSpacing: '0.1px',
};

export const dialogText = {
  color: gray100,
  fontFamily: 'Noto Sans JP',
  fontSize: '16px',
  fontStyle: 'normal',
  fontWeight: 400,
  lineHeight: 'normal',
  letterSpacing: '0.1px',
};

/**
 * アカウント管理の情報行に置く操作ボタンの幅（#3132）。
 * アイコンの有無で実幅がずれないよう、固定幅で揃える。
 */
export const infoRowActionButtonWidth = '88px';

/**
 * 企業詳細画面のカード内 2 カラムに使う左右の間隔（#3310）。
 *
 * 上段（契約情報・会社情報）は「半幅カード 2 枚 + カード間 24px」なのに対し、
 * オプション機能は「全幅カード 1 枚の中を 2 分割」という別構造のため、
 * 内側に同じ 24px を入れると各カラムの端が上段のカード内容と 24px ずれる。
 *
 * カード左右の padding(24px × 2) + カード間の 24px = 72px を内側の間隔にすると、
 * 2 カラムの左右端が上段カードの内容の端とちょうど揃う。
 */
export const cardInnerColumnGutter = '72px';

/**
 * createTheme に渡す Button / IconButton のホバー設定（#3310）。
 *
 * #3153 で palette.primary をブランドのオレンジにした副作用で、色を明示していない
 * Button（text variant）と IconButton のホバー背景が MUI 既定の
 * `alpha(palette.primary.main, 0.04)` によってオレンジになってしまった。
 * 「次へ」「前へ」「×」など、ブランド色を主張する意図のないボタンまで着色されるため、
 * 既定のホバーはグレーに戻す。
 *
 * sx で個別にホバーを指定している箇所（塗りのオレンジボタン等）は
 * そちらの詳細度が高いため、この設定の影響を受けない。
 */
export const baseButtonHoverStyle = {
  MuiButton: {
    styleOverrides: {
      // 塗り・枠線ありのボタンは個別に色を持つため、色指定のない text variant のみ対象にする
      text: {
        '&:hover': {
          backgroundColor: black_overlay4,
        },
      },
    },
  },
  MuiIconButton: {
    styleOverrides: {
      root: {
        '&:hover': {
          backgroundColor: black_overlay4,
        },
      },
    },
  },
};
